import { SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Select,
  Table,
  TableProps,
  Tooltip,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import StatBand from "components/StatBand.component";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import { getProductMembers } from "hooks/queries/product";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { th, tnum } from "shared/components/TableText";
import { personFilterOption } from "shared/components/PersonSelectOption";
import { PAGE_SIZE } from "shared/constants";
import PATHS from "shared/constants/paths";
import { textPx } from "shared/constants/zoom";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { PackageProductMember } from "shared/types";
import {
  ACCOUNT_STATUS_LABELS,
  ACCOUNT_STATUS_TONE,
  SHARED_LABEL,
} from "shared/utils";
import { localTzDate } from "shared/utils/dayjs";
import { formatPhone } from "shared/utils/phone";

// The product page's Paket Aktif / Membership Aktif tab: every package of
// the product a client can still book with, one row per package under its
// owner (a shared package has one balance and one end date), in the list
// pages' shape (stat band + table). Search, status and tier live in the URL
// (replace) and the page in useRememberedTablePage, so Back from a client's
// package lands on the same slice of the list.

type HolderStatus = "all" | "expiring" | "low_credit" | "unactivated";
const STATUS_KEYS: readonly HolderStatus[] = [
  "all",
  "expiring",
  "low_credit",
  "unactivated",
];

const matchesStatus = (row: PackageProductMember, status: HolderStatus) =>
  status === "all" ||
  (status === "expiring" && row.expiring_soon) ||
  (status === "low_credit" && !!row.low_credit) ||
  (status === "unactivated" && !!row.unactivated);

// The Status Paket report's own words, so one state has one name. The two
// alert states name the studio setting behind them: Akan Kedaluwarsa
// (7 Hari), Kredit Akan Habis (2 Kredit).
type AlertWindows = { expiringSoonDays?: number; lowCreditThreshold?: number };
const countLabel = (keyBase: string, n: number) =>
  translateWithVars(n === 1 ? `${keyBase}_one` : `${keyBase}_other`, { n });
const statusLabel = (status: Exclude<HolderStatus, "all">, windows: AlertWindows) =>
  status === "expiring"
    ? windows.expiringSoonDays
      ? countLabel("product_holders.expiring_days", windows.expiringSoonDays)
      : translate("analyticsPage.package.view_expiring_soon")
    : status === "low_credit"
      ? windows.lowCreditThreshold
        ? countLabel("product_holders.low_credit", windows.lowCreditThreshold)
        : translate("analyticsPage.package.view_low_credits")
      : translate("analyticsPage.package.view_unactivated");

const tierLabel = (credits: number) =>
  translateWithVars(
    credits === 1 ? "product_holders.tier_one" : "product_holders.tier_other",
    { n: credits },
  );

const formatDate = (iso?: string | null) =>
  iso ? localTzDate(iso).format("DD/MM/YYYY") : "-";

const packagePath = (row: PackageProductMember) =>
  `${PATHS.CUSTOMER_PACKAGE}/${row.customer_package_id}`;

// Timestamps, not localeCompare: collation puts punctuation before digits,
// so a "last" sentinel string would sort first.
const endSortKey = (row: PackageProductMember) =>
  row.unactivated || !row.expiry_date
    ? Number.MAX_SAFE_INTEGER
    : new Date(row.expiry_date).getTime();

const rowKey = (row: PackageProductMember) => row.customer_package_id;

// Kredit Tersedia = Tersisa minus held (getActualRemainingCredits' rule).
const availableCredits = (row: PackageProductMember) =>
  Math.max(0, (row.remaining_credits ?? 0) - (row.onhold_credits ?? 0));

export const holdersTitle = (isMembership: boolean) =>
  isMembership
    ? translate("product_holders.title_membership")
    : translate("product_holders.title_package");

// Search finds a package by its owner or by anyone it is shared with, so a
// co-sharer stays findable although their name is not always on the row.
const matchesQuery = (row: PackageProductMember, query: string) =>
  [
    { label: row.customer_name, phone: row.phone_number ?? undefined },
    ...(row.shared_with ?? []).map((member) => ({
      label: member.name,
      phone: member.phone_number ?? undefined,
    })),
  ].some((person) => personFilterOption(query, person));

export const useProductHolders = (productId: string | undefined) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading } = getProductMembers(productId, !!productId);
  const rows = useMemo(() => data ?? [], [data]);
  const { page, handleTableChange } = useRememberedTablePage(
    productId ? `productHolders.${productId}` : undefined,
  );

  // The search box is local state (instant filtering) and reaches the URL
  // after a pause, so typing does not re-render the whole form page per key.
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  const rawStatus = searchParams.get("status") as HolderStatus | null;
  const status: HolderStatus =
    rawStatus && STATUS_KEYS.includes(rawStatus) ? rawStatus : "all";

  // Tiers are told apart by credit count, the way they are named: a deleted
  // tier and the live one that replaced it can both be "20 sesi", and a
  // client holding either is a 20-session holder.
  const tierOptions = useMemo(() => {
    const credits = new Set<number>();
    rows.forEach((row) => {
      if (row.tier_credits != null) credits.add(row.tier_credits);
    });
    return Array.from(credits)
      .sort((a, b) => a - b)
      .map((n) => ({ value: n, label: tierLabel(n) }));
  }, [rows]);
  const rawTier = Number(searchParams.get("tier"));
  const tier = tierOptions.some((option) => option.value === rawTier)
    ? rawTier
    : null;

  const writeParams = (patch: Record<string, string | null>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([name, value]) => {
          if (value) next.set(name, value);
          else next.delete(name);
        });
        return next;
      },
      // Keep the state the page arrived with (returnTo).
      { replace: true, state: location.state },
    );
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if ((searchParams.get("q") ?? "") !== query.trim()) {
        writeParams({ q: query.trim() || null });
      }
    }, 300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Search and tier narrow everything; the status counts are taken after
  // them, so each segment shows how many rows tapping it would leave.
  const narrowed = useMemo(
    () =>
      rows.filter(
        (row) =>
          (tier == null || row.tier_credits === tier) && matchesQuery(row, query),
      ),
    [rows, tier, query],
  );
  const counts = useMemo(() => {
    const result: Record<HolderStatus, number> = {
      all: narrowed.length,
      expiring: 0,
      low_credit: 0,
      unactivated: 0,
    };
    narrowed.forEach((row) => {
      if (matchesStatus(row, "expiring")) result.expiring += 1;
      if (matchesStatus(row, "low_credit")) result.low_credit += 1;
      if (matchesStatus(row, "unactivated")) result.unactivated += 1;
    });
    return result;
  }, [narrowed]);
  const filtered = useMemo(
    () => narrowed.filter((row) => matchesStatus(row, status)),
    [narrowed, status],
  );

  // A status gets a segment only when some row of this product is in it
  // (or it is the selected one, so a restored filter stays visible to
  // clear). Read from the whole list, not the searched one, so segments do
  // not come and go while the admin types.
  const statuses = useMemo(
    () =>
      STATUS_KEYS.filter(
        (key) =>
          key === "all" ||
          key === status ||
          rows.some((row) => matchesStatus(row, key)),
      ),
    [rows, status],
  );

  const resetPage = () => handleTableChange({ current: 1 });
  const setStatus = (next: HolderStatus) => {
    // Re-tapping the selected slice widens back to everyone (StatBand idiom).
    writeParams({ status: next === "all" || next === status ? null : next });
    resetPage();
  };
  const setTier = (next: number | null) => {
    writeParams({ tier: next == null ? null : String(next) });
    resetPage();
  };
  const changeQuery = (next: string) => {
    setQuery(next);
    resetPage();
  };
  const filtersActive = !!query.trim() || status !== "all" || tier != null;
  const resetFilters = () => {
    setQuery("");
    writeParams({ q: null, status: null, tier: null });
    resetPage();
  };

  // A remembered page past the end (the list shrank since) shows the last.
  const lastPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page ?? 1, lastPage);

  return {
    rows,
    filtered,
    isLoading,
    query,
    changeQuery,
    status,
    setStatus,
    statuses,
    counts,
    tier,
    setTier,
    tierOptions,
    filtersActive,
    resetFilters,
    currentPage,
    handleTableChange,
  };
};

type Holders = ReturnType<typeof useProductHolders>;

const NameBadges = ({ row }: { row: PackageProductMember }) => (
  <>
    {(row.shared_with?.length ?? 0) > 0 && (
      <StatusBadge tone="progress" label={SHARED_LABEL} dot={false} />
    )}
    {row.client_active === false && (
      <StatusBadge
        tone={ACCOUNT_STATUS_TONE.inactive}
        label={ACCOUNT_STATUS_LABELS.inactive}
      />
    )}
  </>
);

const captionStyle = { fontSize: textPx(12.5), color: colors.ink2 };
const caption = (text: string) => <div style={captionStyle}>{text}</div>;

// Who else uses the package: the one name, or a count with every name on
// hover once there are two or more.
const SharedWithLine = ({ row }: { row: PackageProductMember }) => {
  const members = row.shared_with ?? [];
  if (members.length === 0) return null;
  if (members.length === 1) {
    return caption(
      translateWithVars("product_holders.shared_with_one", { name: members[0].name }),
    );
  }
  return (
    <div style={captionStyle}>
      <Tooltip title={members.map((member) => member.name).join(", ")}>
        <span style={{ cursor: "help" }}>
          {translateWithVars("product_holders.shared_with_other", { n: members.length })}
        </span>
      </Tooltip>
    </div>
  );
};

const ProductHolders = ({
  holders,
  isMembership,
  expiringSoonDays,
  lowCreditThreshold,
}: {
  holders: Holders;
  isMembership: boolean;
  /** The studio's Akan Kedaluwarsa window, from the product DTO. */
  expiringSoonDays?: number;
  /** The studio's Kredit Akan Habis threshold, from the product DTO. */
  lowCreditThreshold?: number;
}) => {
  const navigate = useNavigate();
  const showTier = !isMembership && holders.tierOptions.length > 1;

  const columns: TableProps<PackageProductMember>["columns"] = [
    {
      title: th(translate("analyticsPage.columns.client")),
      dataIndex: "customer_name",
      sorter: (a, b) => a.customer_name.localeCompare(b.customer_name),
      render: (_: unknown, row: PackageProductMember) => (
        <div style={{ minWidth: 0 }}>
          <Flex align="center" gap={6} wrap="wrap">
            {/* A real link (Tab, Enter, ctrl-click, middle-click); the row's
                own click covers the rest of the row. */}
            <Link
              to={packagePath(row)}
              onClick={(event) => event.stopPropagation()}
              className="holder-name-link"
            >
              {row.customer_name}
            </Link>
            <NameBadges row={row} />
          </Flex>
          <SharedWithLine row={row} />
        </div>
      ),
    },
    {
      title: th(translate("label.phone_number")),
      dataIndex: "phone_number",
      width: "16%",
      render: (phone: string | null) => (
        // A number never breaks across lines; a narrow window scrolls the
        // table instead (scroll.x below).
        <span style={{ ...tnum, color: colors.ink2, whiteSpace: "nowrap" }}>
          {formatPhone(phone) || "-"}
        </span>
      ),
    },
    ...(showTier
      ? [
          {
            title: th(translate("product_catalog.label_tier")),
            dataIndex: "tier_credits",
            width: "10%",
            render: (credits: number | null) =>
              credits == null ? "-" : tierLabel(credits),
          },
        ]
      : []),
    ...(isMembership
      ? []
      : [
          {
            // Kredit Tersedia, as on the client's package page: what the
            // client can still book with; held credits are the caption.
            title: th(translate("label.available_session")),
            dataIndex: "remaining_credits",
            width: "13%",
            sorter: (a: PackageProductMember, b: PackageProductMember) =>
              availableCredits(a) - availableCredits(b),
            render: (_: unknown, row: PackageProductMember) => (
              <div>
                <span style={tnum}>
                  {`${availableCredits(row)} / ${row.total_credits ?? 0}`}
                </span>
                {(row.onhold_credits ?? 0) > 0 &&
                  caption(
                    translateWithVars("label.onhold_caption", { n: row.onhold_credits ?? 0 }),
                  )}
              </div>
            ),
          },
        ]),
    {
      title: th(translate("label.purchase_date")),
      dataIndex: "purchase_date",
      width: "14%",
      sorter: (a, b) =>
        new Date(a.purchase_date).getTime() - new Date(b.purchase_date).getTime(),
      render: (iso: string) => <span style={tnum}>{formatDate(iso)}</span>,
    },
    {
      title: th(translate("analyticsPage.columns.expiry")),
      dataIndex: "expiry_date",
      width: "18%",
      defaultSortOrder: "ascend",
      // Soonest end first; a package whose clock has not started, and one
      // that never ends, after every dated one (the server's order too).
      sorter: (a, b) => endSortKey(a) - endSortKey(b),
      render: (_: unknown, row: PackageProductMember) =>
        row.unactivated ? (
          <div>
            <span style={{ color: colors.ink2 }}>{translate("label.not_activated")}</span>
            {row.auto_start_date &&
              caption(
                translateWithVars("label.auto_start_on", {
                  date: formatDate(row.auto_start_date),
                }),
              )}
          </div>
        ) : (
          // Plain like every other date: the Akan Kedaluwarsa filter is where
          // expiring packages are picked out (Andrew, 2026-10-08).
          <span style={tnum}>{formatDate(row.expiry_date)}</span>
        ),
    },
  ];

  const emptyText = holders.filtersActive ? (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={translate("product_holders.no_match")}
    >
      <Button onClick={holders.resetFilters}>
        {translate("product_holders.reset_filters")}
      </Button>
    </Empty>
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        isMembership
          ? translate("product_holders.empty_membership")
          : translate("product_holders.empty_package")
      }
    />
  );
  const hasRows = holders.rows.length > 0;

  return (
    <Flex vertical gap={20}>
      {/* One segment alone would only repeat the tab label's count. */}
      {holders.statuses.length > 1 && (
        <StatBand
          segments={holders.statuses.map((key) => ({
            key,
            value: holders.counts[key],
            label:
              key === "all"
                ? holdersTitle(isMembership)
                : statusLabel(key, { expiringSoonDays, lowCreditThreshold }),
          }))}
          selectedKey={holders.status}
          onSelect={(key) => holders.setStatus(key as HolderStatus)}
        />
      )}
      <Card styles={{ body: { padding: 18 } }}>
        {(hasRows || holders.filtersActive) && (
          <Flex gap={8} wrap="wrap" style={{ marginBottom: 14, maxWidth: 560 }}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: colors.ink3 }} />}
              placeholder={translate("product_holders.search_placeholder")}
              aria-label={translate("product_holders.search_placeholder")}
              value={holders.query}
              onChange={(event) => holders.changeQuery(event.target.value)}
              style={{ flex: "1 1 240px", minWidth: 0 }}
            />
            {showTier && (
              <Select
                value={holders.tier ?? "all"}
                onChange={(value) =>
                  holders.setTier(value === "all" ? null : Number(value))
                }
                aria-label={translate("product_catalog.label_tier")}
                style={{ flex: "0 0 160px" }}
                options={[
                  { value: "all", label: translate("product_holders.all_tiers") },
                  ...holders.tierOptions,
                ]}
              />
            )}
          </Flex>
        )}
        <Table<PackageProductMember>
          showSorterTooltip={false}
          rowKey={rowKey}
          size="middle"
          loading={holders.isLoading}
          columns={columns}
          dataSource={holders.filtered}
          scroll={{ x: 820 }}
          locale={{ emptyText }}
          onRow={(row) => ({
            onClick: () => navigate(packagePath(row)),
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: holders.currentPage,
            pageSize: PAGE_SIZE,
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
          onChange={(pagination) => holders.handleTableChange(pagination)}
        />
      </Card>
    </Flex>
  );
};

export default ProductHolders;
